import { api } from '@web/api/client'
import type { SessionHistoryItem, SessionRecord } from '@web/api/types'
import { useDialogFocus } from '@web/lib/useDialogFocus'
import { useRef, useState } from 'react'

export function RenameSession({
  session,
  onClose,
  onSaved,
  returnFocus,
}: {
  returnFocus?: HTMLElement | null
  session: SessionHistoryItem
  onClose: () => void
  onSaved: (record: SessionRecord) => void
}) {
  const [title, setTitle] = useState(session.title)
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState('')
  const dialog = useRef<HTMLDivElement>(null)
  useDialogFocus(dialog, onClose, !busy, returnFocus)
  return (
    <div
      ref={dialog}
      tabIndex={-1}
      className="session-rename fixed inset-0 z-50 grid place-items-center bg-black/40 p-4"
      role="dialog"
      aria-modal="true"
      aria-label="Rename session"
    >
      <form
        className="card w-full max-w-md p-6"
        onSubmit={async (event) => {
          event.preventDefault()
          setBusy(true)
          setError('')
          try {
            onSaved(await api.renameSession(session.id, title.trim()))
            onClose()
          } catch (failure) {
            setError((failure as Error).message)
            setBusy(false)
          }
        }}
      >
        <h2 className="text-lg font-semibold mb-3">Rename session</h2>
        <input
          className="control w-full"
          aria-label="Session title"
          required
          maxLength={160}
          value={title}
          onChange={(event) => setTitle(event.target.value)}
        />
        <p className="text-xs text-muted mt-3">
          Saving keeps this name and turns off automatic naming.
        </p>
        {error && (
          <p role="alert" className="text-danger text-sm mt-3">
            {error}
          </p>
        )}
        <div className="mt-5 flex justify-end gap-2">
          <button
            type="button"
            className="btn"
            onClick={onClose}
            disabled={busy}
          >
            Cancel
          </button>
          <button className="btn btn-primary" disabled={busy || !title.trim()}>
            {busy ? 'Saving…' : 'Save name'}
          </button>
        </div>
      </form>
    </div>
  )
}
